import { useEffect, useState } from 'react'
import { Alert, Col, Container, Form, Row } from 'react-bootstrap'
import KLayerNavigation from '../components/KLayerNavigation'
import KButton from '../components/KButton'
import { isUserLoggedIn, showKAlert } from '../helpers'
import { t } from '../locale'

export interface MicrosoftAppStatus {
  configured: boolean
  clientId?: string
  tenantId?: string
  redirectUri?: string
  updatedAt?: string
}

export interface MicrosoftAppSettingsConfig {
  // Locale keys (kept as keys so the shared component stays translation-agnostic).
  titleKey: string
  hintKey: string
  clientIdLabelKey: string
  tenantIdLabelKey: string
  clientSecretLabelKey: string
  redirectUriLabelKey: string
  connectedKey: string
  notConnectedKey: string
  saveKey: string
  disconnectKey: string
  allFieldsRequiredKey: string
  savedMessageKey: string
  // Element ids keep their per-page value (e.g. btn-microsoft-sso-save).
  idPrefix: string
  // The SSO registration requires a tenant id; the SharePoint app registration doesn't.
  tenantIdRequired: boolean
  guide?: { href: string; labelKey: string }
  loadStatus: () => Promise<MicrosoftAppStatus>
  saveConnection: (payload: {
    clientId: string
    tenantId: string
    clientSecret: string
  }) => Promise<{ ok?: boolean }>
  deleteConnection: () => Promise<{ ok?: boolean }>
}

const emptyForm = { clientId: '', tenantId: '', clientSecret: '' }

/**
 * Shared UI for the two per-tenant Microsoft Entra app registrations (SSO and
 * Microsoft 365 / SharePoint). They differ only in labels, endpoints and whether
 * a tenant id is required, so both pages render this with a config.
 */
export default function MicrosoftAppSettings({ config }: { config: MicrosoftAppSettingsConfig }) {
  const [status, setStatus] = useState<MicrosoftAppStatus | null>(null)
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    void fetchStatus()
    // Mount-only initial load; fetchStatus is re-created each render by design.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchStatus = async () => {
    try {
      setStatus(await config.loadStatus())
    } catch {
      setStatus(null)
    }
  }

  const save = async () => {
    const missingFields =
      !form.clientId.trim() ||
      !form.clientSecret.trim() ||
      (config.tenantIdRequired && !form.tenantId.trim())

    if (missingFields) {
      showKAlert('danger', t(config.allFieldsRequiredKey))
      return
    }

    setSaving(true)
    try {
      const res = await config.saveConnection({
        clientId: form.clientId.trim(),
        tenantId: form.tenantId.trim(),
        clientSecret: form.clientSecret.trim(),
      })
      if (res.ok) {
        setForm(emptyForm)
        showKAlert('success', config.savedMessageKey)
        await fetchStatus()
      }
    } finally {
      setSaving(false)
    }
  }

  const disconnect = async () => {
    const res = await config.deleteConnection()
    if (res.ok) {
      setForm(emptyForm)
      setStatus({ configured: false })
    }
  }

  return (
    <Container>
      <Row>
        <div className='sidebar col-md-3 col-lg-2 p-0'>
          <KLayerNavigation logged={isUserLoggedIn()} />
        </div>
        <main className='ms-sm-auto col-lg-10 px-md-4 md-4'>
          <div className='col-12'>
            <p className='pe-none text-draft lead fw-bold link-offset-3 link-underline-primary'>
              {t(config.titleKey)}
            </p>
          </div>

          <div className='col-12 my-5'>
            <Row>
              <Col lg={4}>
                <div className='d-flex justify-between section-title-blade'>
                  <div className='pe-4 sm:px-0'>
                    <p className='mt-1 text-draft'>{t(config.hintKey)}</p>

                    {config.guide && (
                      <a
                        className='btn btn-sm btn-outline-secondary mt-2'
                        href={config.guide.href}
                        target='_blank'
                        rel='noopener noreferrer'
                      >
                        <i className='bi bi-file-earmark-pdf me-1' />
                        {t(config.guide.labelKey)}
                      </a>
                    )}
                  </div>
                </div>
              </Col>
              <Col lg={8}>
                <div className='px-4 py-5 bg-white sm:p-6 shadow sm:rounded-tl-md sm:rounded-tr-md'>
                  {status?.configured && (
                    <Alert
                      variant='success'
                      className='d-flex justify-content-between align-items-center'
                    >
                      <span>
                        <i className='bi bi-check-circle-fill me-2'></i>
                        {t(config.connectedKey)}
                        {status.clientId ? ` — ${status.clientId}` : ''}
                        {status.tenantId ? ` / ${status.tenantId}` : ''}
                      </span>
                      <KButton
                        id={`btn-${config.idPrefix}-disconnect`}
                        label={t(config.disconnectKey)}
                        variant='outline-danger'
                        icon='x-circle'
                        size='sm'
                        onClick={disconnect}
                      />
                    </Alert>
                  )}
                  {status && !status.configured && (
                    <p className='text-muted'>{t(config.notConnectedKey)}</p>
                  )}

                  <Form>
                    <Form.Group className='mb-3'>
                      <Form.Label className='fw-medium smaller text-primary'>
                        {t(config.clientIdLabelKey)}
                      </Form.Label>
                      <Form.Control
                        type='text'
                        value={form.clientId}
                        onChange={e => setForm({ ...form, clientId: e.target.value })}
                      />
                    </Form.Group>
                    <Form.Group className='mb-3'>
                      <Form.Label className='fw-medium smaller text-primary'>
                        {t(config.tenantIdLabelKey)}
                      </Form.Label>
                      <Form.Control
                        type='text'
                        value={form.tenantId}
                        onChange={e => setForm({ ...form, tenantId: e.target.value })}
                      />
                    </Form.Group>
                    <Form.Group className='mb-3'>
                      <Form.Label className='fw-medium smaller text-primary'>
                        {t(config.clientSecretLabelKey)}
                      </Form.Label>
                      <Form.Control
                        type='password'
                        autoComplete='new-password'
                        value={form.clientSecret}
                        onChange={e => setForm({ ...form, clientSecret: e.target.value })}
                      />
                    </Form.Group>
                    <Form.Group className='mb-3'>
                      <Form.Label className='fw-medium smaller text-primary'>
                        {t(config.redirectUriLabelKey)}
                      </Form.Label>
                      <Form.Control type='text' readOnly value={status?.redirectUri ?? ''} />
                    </Form.Group>
                    <KButton
                      id={`btn-${config.idPrefix}-save`}
                      label={t(config.saveKey)}
                      variant='primary'
                      icon='plug'
                      disabled={saving}
                      onClick={save}
                    />
                  </Form>
                </div>
              </Col>
            </Row>
          </div>
        </main>
      </Row>
    </Container>
  )
}
